import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Empty, DatePicker, Select, Button, message } from 'antd';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  FireOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import { 
  StyledAnalyticsTabs, 
  TabsAndFiltersRow, 
  FiltersContainer, 
  ChartCard, 
  StatCard, 
  StatsRow, 
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  valueColors,
  suffixStyles,
  iconColors
} from '../styled';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Booking = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('funnel');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(30, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || 'month');
  
  const [funnelData, setFunnelData] = useState(null);
  const [cancellationData, setCancellationData] = useState(null);
  const [timeSlotData, setTimeSlotData] = useState(null);
  const [leadTimeData, setLeadTimeData] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/booking-data', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booking-data-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('Booking data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export booking data');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPageHeader({ title: 'Booking Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        timeRange: quickRange,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD')
      };

      const [funnelRes, cancelRes, slotRes, leadRes] = await Promise.all([
        api.get('/screening/analytics/booking/funnel', params),
        api.get('/screening/analytics/booking/cancellations', params),
        api.get('/screening/analytics/booking/time-slots', params),
        api.get('/screening/analytics/booking/lead-time', params)
      ]);

      // Handle both response formats: { success, data } or direct data object
      const extractData = (res) => res.data?.success !== undefined 
        ? (res.data.success ? res.data.data : null) 
        : res.data;

      if (extractData(funnelRes)) setFunnelData(extractData(funnelRes));
      if (extractData(cancelRes)) setCancellationData(extractData(cancelRes));
      if (extractData(slotRes)) setTimeSlotData(extractData(slotRes));
      if (extractData(leadRes)) setLeadTimeData(extractData(leadRes));
    } catch (error) {
      console.error('Error loading booking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFunnel = () => {
    if (!funnelData) return <Empty description="No data available" />;

    // Funnel bar chart (horizontal)
    const funnelChartOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { 
        bar: { 
          horizontal: true, 
          distributed: true,
          borderRadius: 4,
          dataLabels: { position: 'center' }
        } 
      },
      colors: funnelData.funnelData.map(d => d.color),
      dataLabels: { 
        enabled: true,
        formatter: (val) => val,
        style: { fontSize: '12px', colors: ['#fff'] }
      },
      xaxis: { categories: funnelData.funnelData.map(d => d.stage) },
      legend: { show: false },
      tooltip: {
        y: { formatter: (val) => `${val} bookings` }
      }
    };
    const funnelSeries = [{ name: 'Bookings', data: funnelData.funnelData.map(d => d.count) }];

    // Status pie chart
    const statusFiltered = funnelData.statusBreakdown.filter(s => s.count > 0);
    const pieChartOptions = {
      chart: { type: 'donut' },
      labels: statusFiltered.map(s => s.status),
      colors: ['#faad14', '#1890ff', '#108043', '#b91c1c', '#ff7a45', '#722ed1'],
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      },
      tooltip: {
        y: { formatter: (val) => `${val} bookings` }
      }
    };
    const pieSeries = statusFiltered.map(s => s.count);

    // Trend area chart
    const trendChartOptions = {
      chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 2 },
      colors: ['#1890ff', '#108043'],
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 }
      },
      xaxis: { 
        categories: funnelData.bookingTrend?.map(d => dayjs(d.date).format('MMM D')) || [],
        labels: { rotate: -45 }
      },
      legend: { position: 'top' },
      tooltip: { shared: true }
    };
    const trendSeries = [
      { name: 'Total', data: funnelData.bookingTrend?.map(d => d.total) || [] },
      { name: 'Confirmed', data: funnelData.bookingTrend?.map(d => d.confirmed) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Bookings"
                value={funnelData.summary.totalBookings}
                prefix={<CalendarOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Confirmed"
                value={funnelData.summary.confirmed}
                valueStyle={{ color: valueColors.success }}
                prefix={<CheckCircleOutlined />}
                suffix={<span style={suffixStyles}>{funnelData.summary.confirmationRate}%</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Completed"
                value={funnelData.summary.completed}
                valueStyle={{ color: valueColors.cyan }}
                suffix={<span style={suffixStyles}>{funnelData.summary.completionRate}%</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Cancellation Rate"
                value={funnelData.summary.cancellationRate}
                suffix="%"
                valueStyle={{ color: parseFloat(funnelData.summary.cancellationRate) > 20 ? '#b91c1c' : '#108043' }}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Booking Funnel">
              <Chart options={funnelChartOptions} series={funnelSeries} type="bar" height={350} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Status Breakdown">
              <Chart options={pieChartOptions} series={pieSeries} type="donut" height={350} />
            </ChartCard>
          </Col>
        </ChartsRow>

        {funnelData.bookingTrend?.length > 0 && (
          <ChartCard title="Daily Booking Trend">
            <Chart options={trendChartOptions} series={trendSeries} type="area" height={300} />
          </ChartCard>
        )}
      </>
    );
  };

  const renderCancellation = () => {
    if (!cancellationData) return <Empty description="No data available" />;

    // Cancellation reasons (horizontal bar)
    const reasonsOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ['#b91c1c'],
      xaxis: { categories: cancellationData.reasonsBreakdown?.slice(0, 8).map(r => r.reason) || [] },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (val) => `${val} cancellations` } }
    };
    const reasonsSeries = [{ name: 'Cancellations', data: cancellationData.reasonsBreakdown?.slice(0, 8).map(r => r.count) || [] }];

    // Timing distribution
    const timingOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#faad14'],
      xaxis: { categories: cancellationData.timingDistribution?.map(t => t.timing) || [] },
      dataLabels: { enabled: false }
    };
    const timingSeries = [{ name: 'Cancellations', data: cancellationData.timingDistribution?.map(t => t.count) || [] }];

    // By day of week
    const dayOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
      colors: ['#722ed1'],
      xaxis: { categories: cancellationData.cancellationsByDay?.map(d => d.day) || [] },
      dataLabels: { enabled: false }
    };
    const daySeries = [{ name: 'Cancellations', data: cancellationData.cancellationsByDay?.map(d => d.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Cancellations"
                value={cancellationData.summary.totalCancellations}
                valueStyle={{ color: valueColors.danger }}
                prefix={<CloseCircleOutlined />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Top Reason"
                value={cancellationData.summary.topReason?.substring(0, 20) || 'N/A'}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Same Day Cancellations"
                value={cancellationData.summary.sameDayCancellations}
                prefix={<ClockCircleOutlined style={iconColors.warning} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Same Day Rate"
                value={cancellationData.summary.sameDayRate}
                suffix="%"
                valueStyle={{ color: parseFloat(cancellationData.summary.sameDayRate) > 30 ? '#b91c1c' : '#108043' }}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Cancellation Reasons">
              {cancellationData.reasonsBreakdown?.length > 0 ? (
                <Chart options={reasonsOptions} series={reasonsSeries} type="bar" height={300} />
              ) : (
                <Empty description="No cancellation data" />
              )}
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Cancellation Timing">
              <Chart options={timingOptions} series={timingSeries} type="bar" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>

        <ChartCard title="Cancellations by Day of Week">
          <Chart options={dayOptions} series={daySeries} type="bar" height={250} />
        </ChartCard>
      </>
    );
  };

  const renderTimeSlots = () => {
    if (!timeSlotData) return <Empty description="No data available" />;

    // Hourly popularity
    const hourlyOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '70%' } },
      colors: ['#1890ff'],
      xaxis: { categories: timeSlotData.hourlyPopularity?.map(h => h.label) || [] },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (val) => `${val} bookings` } }
    };
    const hourlySeries = [{ name: 'Bookings', data: timeSlotData.hourlyPopularity?.map(h => h.count) || [] }];

    // Day popularity
    const dayOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#108043'],
      xaxis: { categories: timeSlotData.dayPopularity?.map(d => d.day) || [] },
      dataLabels: { enabled: false }
    };
    const daySeries = [{ name: 'Bookings', data: timeSlotData.dayPopularity?.map(d => d.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Total Bookings"
                value={timeSlotData.summary.totalBookings}
                prefix={<CalendarOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Peak Hour"
                value={timeSlotData.summary.peakHour}
                prefix={<FireOutlined style={iconColors.danger} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Peak Day"
                value={timeSlotData.summary.peakDay}
                prefix={<CalendarOutlined style={iconColors.success} />}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Bookings by Hour">
              <Chart options={hourlyOptions} series={hourlySeries} type="bar" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Bookings by Day of Week">
              <Chart options={dayOptions} series={daySeries} type="bar" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>

        {timeSlotData.peakHours?.length > 0 && (
          <ChartCard title="Top 3 Peak Hours">
            <Row gutter={16}>
              {timeSlotData.peakHours.map((peak, idx) => (
                <Col xs={24} sm={8} key={idx}>
                  <Card style={{ textAlign: 'center', background: idx === 0 ? '#fff7e6' : '#f6ffed' }}>
                    <Statistic
                      title={`#${idx + 1} Peak Hour`}
                      value={peak.label}
                      suffix={<span style={suffixStyles}>({peak.count} bookings)</span>}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </ChartCard>
        )}
      </>
    );
  };

  const renderLeadTime = () => {
    if (!leadTimeData) return <Empty description="No data available" />;

    // Lead time distribution
    const leadOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { 
        bar: { 
          borderRadius: 4, 
          columnWidth: '60%',
          distributed: true
        } 
      },
      colors: ['#1890ff', '#13c2c2', '#108043', '#faad14', '#722ed1'],
      xaxis: { categories: leadTimeData.distribution?.map(d => d.range) || [] },
      dataLabels: { enabled: true, style: { fontSize: '11px' } },
      legend: { show: false },
      tooltip: { y: { formatter: (val) => `${val} bookings` } }
    };
    const leadSeries = [{ name: 'Bookings', data: leadTimeData.distribution?.map(d => d.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Bookings"
                value={leadTimeData.summary.totalBookings}
                prefix={<CalendarOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Avg Lead Time"
                value={leadTimeData.summary.avgLeadTime}
                suffix=" days"
                prefix={<FieldTimeOutlined style={iconColors.purple} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Median Lead Time"
                value={leadTimeData.summary.medianLeadTime}
                suffix=" days"
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Same Day Bookings"
                value={leadTimeData.summary.sameDayBookings}
                suffix={<span style={suffixStyles}>{leadTimeData.summary.sameDayRate}%</span>}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartCard title="Lead Time Distribution">
          <Chart options={leadOptions} series={leadSeries} type="bar" height={350} />
        </ChartCard>
      </>
    );
  };

  const subTabItems = [
    { key: 'funnel', label: 'Booking Funnel' },
    { key: 'cancellations', label: 'Cancellations' },
    { key: 'time-slots', label: 'Time Slot Popularity' },
    { key: 'lead-time', label: 'Lead Time' }
  ];

  const renderContent = () => {
    switch (activeSubTab) {
      case 'cancellations': return renderCancellation();
      case 'time-slots': return renderTimeSlots();
      case 'lead-time': return renderLeadTime();
      default: return renderFunnel();
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '7days': newRange = [now.subtract(7, 'days'), now]; break;
      case 'month': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case '6months': newRange = [now.subtract(6, 'months'), now]; break;
      case 'year': newRange = [now.subtract(1, 'year'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  if (loading && !funnelData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs activeKey={activeSubTab} onChange={setActiveSubTab} items={subTabItems} />
        <FiltersContainer>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={selectStyles} size="large">
            <Option value="7days">Last 7 days</Option>
            <Option value="month">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="6months">Last 6 months</Option>
            <Option value="year">Last year</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker value={dateRange} onChange={handleDateRangeChange} format="MM/DD/YY" style={rangePickerStyles} size="large" allowClear={false} />
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} size="large">Export</Button>
        </FiltersContainer>
      </TabsAndFiltersRow>

      {loading ? <HeartLoader size="32px" padding="40px" /> : <div className="content-animate-in">{renderContent()}</div>}
    </PageContainer>
  );
};

export default Booking;
